/** @file Inspector: pose, node, rig, drawable and facet-entry sections for the current selection. */
import type { ReactElement } from 'react';
import { useMemo, useState } from 'react';
import { useEditor } from '../../state/useEditor';
import { useEditorStore } from '../../state/useEditorStore';
import { artClient } from '../art/artClient';
import type { ArtDeps } from '../art/artTypes';
import type { ResizeEnv } from '../art/fit/applyEntryResize';
import { DrawableSection } from './DrawableSection';
import { FacetEntrySection } from './FacetEntrySection';
import { NodeSection } from './NodeSection';
import { PoseSection } from './PoseSection';
import { RigSection } from './RigSection';
import { useFitPrompt } from './useFitPrompt';

const MUTED = 'var(--muted, #8b919e)';

/** The facet entry the Rig and Facet entry blocks edit, per drawable (another drawable starts at entry 0). */
function usePickedEntry(drawable: string | null): [number, (entry: number) => void] {
  const [picked, setPicked] = useState<{ drawable: string | null; entry: number }>({ drawable: null, entry: 0 });
  return [picked.drawable === drawable ? picked.entry : 0, (entry) => { setPicked({ drawable, entry }); }];
}

/** The primary node, the part (the selected drawable, else the node's first), whether a document is open, Quads mode. */
function useTarget(): { node: string | null; drawable: string | null; hasDoc: boolean; quads: boolean } {
  const node = useEditor((s) => s.selection.nodes.at(-1) ?? null);
  const picked = useEditor((s) => s.selection.drawable);
  const firstOfNode = useEditor((s) => (node === null ? null : (s.puppet.doc?.puppet.drawables.find((d) => d.node === node)?.id ?? null)));
  const hasDoc = useEditor((s) => s.puppet.doc !== null);
  const quads = useEditor((s) => s.camera.quads);
  return { node, drawable: picked ?? firstOfNode, hasDoc, quads };
}

/** The node's title, Pose and Node blocks. */
function NodeSections({ node }: { node: string }): ReactElement {
  return (
    <>
      <div style={{ padding: '6px 8px', fontWeight: 600 }}>{node}</div>
      <PoseSection node={node} />
      <NodeSection name={node} />
    </>
  );
}

/** The part's Drawable and Facet entry blocks (nothing without a part). */
function PartSections({ drawable, entry, onEntry }: { drawable: string | null; entry: number; onEntry: (entry: number) => void }): ReactElement | null {
  if (drawable === null) return null;
  return <><DrawableSection id={drawable} /><FacetEntrySection id={drawable} entry={entry} onEntry={onEntry} /></>;
}

/**
 * For the primary node `selection.nodes.at(-1)` (the last selected, as the viewport reads it): its title, Pose (the
 * key at the playhead) and Node; for `selection.drawable`, or else the node's first drawable: Rig (first while Quads
 * is on, else after Node), Drawable and Facet entry. `deps` uploads the tiles a size change refits (default: the
 * Art calls of `session.url`; dev pages pass their own); the fit prompt opens over the panel.
 */
export function Inspector({ deps }: { deps?: ArtDeps } = {}): ReactElement {
  // TODO(multi-selection): the store selects one node; with several, differing fields would show a dash here.
  const store = useEditorStore();
  const { node, drawable, hasDoc, quads } = useTarget();
  const url = useEditor((s) => s.session.url);
  const server = useMemo(() => deps ?? artClient(url), [deps, url]);
  const { askFit, prompt } = useFitPrompt();
  const [entry, setEntry] = usePickedEntry(drawable);
  const style = { height: '100%', overflow: 'auto', fontSize: 12, fontFamily: 'var(--font-sans, system-ui, sans-serif)' };
  if (!hasDoc || (node === null && drawable === null)) {
    return <div data-panel="inspector" style={{ ...style, padding: 8, color: MUTED }}>{hasDoc ? 'Select a node' : 'No puppet open'}</div>;
  }
  const env: ResizeEnv = { store, deps: server, askFit };
  const rig = drawable === null ? null : <RigSection id={drawable} entry={entry} env={env} />;
  return (
    <div data-panel="inspector" style={style}>
      {quads && rig}
      {node !== null && <NodeSections node={node} />}
      {!quads && rig}
      <PartSections drawable={drawable} entry={entry} onEntry={setEntry} />
      {prompt}
    </div>
  );
}
